/**
 * Кнопка. Спецификация — docs/04-ДИЗАЙН-СИСТЕМА.md §5.2.1.
 *
 * Классы вариантов взяты из документа дословно: значения там нормативные,
 * и переписывать их «по смыслу» означало бы завести вторую дизайн-систему.
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/shared/lib/cn";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[var(--pt-space-4)] " +
    "rounded-[var(--pt-radius-3)] font-medium whitespace-nowrap select-none " +
    "transition-[background-color,border-color,color] duration-[var(--pt-dur-micro)] " +
    "ease-[var(--pt-ease-standard)] focus-visible:outline-none " +
    "focus-visible:shadow-[var(--pt-ring)] disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--pt-accent)] text-[var(--pt-text-on-accent)] hover:bg-[var(--pt-accent-hover)] active:bg-[var(--pt-accent-active)] disabled:bg-[var(--pt-bg-active)] disabled:text-[var(--pt-text-disabled)]",
        secondary:
          "border border-[var(--pt-border-control)] text-[var(--pt-text-primary)] hover:bg-[var(--pt-bg-hover)] hover:border-[var(--pt-border-strong)] active:bg-[var(--pt-bg-active)] disabled:border-[var(--pt-border-default)] disabled:text-[var(--pt-text-disabled)]",
        ghost:
          "text-[var(--pt-text-secondary)] hover:bg-[var(--pt-bg-hover)] hover:text-[var(--pt-text-primary)] active:bg-[var(--pt-bg-active)] disabled:text-[var(--pt-text-disabled)]",
        danger:
          "bg-[var(--pt-danger)] text-[var(--pt-text-on-accent)] hover:bg-[var(--pt-danger-hover)] active:bg-[var(--pt-danger-active)]",
        dangerGhost: "text-[var(--pt-danger-text)] hover:bg-[var(--pt-danger-soft)]",
        link: "text-[var(--pt-accent-text)] hover:underline underline-offset-2 px-0 min-w-0 h-auto",
      },
      size: {
        sm: "h-[var(--pt-control-h-sm)] px-[var(--pt-space-6)] text-[12px] min-w-[64px]",
        md: "h-[var(--pt-control-h-md)] px-[var(--pt-space-12)] text-[var(--pt-font-ui)] min-w-[88px]",
        lg: "h-[var(--pt-control-h-lg)] px-[var(--pt-space-16)] text-[14px] min-w-[104px]",
      },
      iconOnly: { true: "px-0 aspect-square min-w-0" },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Отрисовать как другой элемент (ссылка в виде кнопки). */
  asChild?: boolean;
  loading?: boolean;
  /** Характер действия. Одновременно с `trailingIcon` использовать нельзя. */
  icon?: React.ReactNode;
  /** Раскрытие меню или переход: ▾, →. */
  trailingIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    iconOnly,
    asChild = false,
    loading = false,
    icon,
    trailingIcon,
    children,
    disabled,
    type = "button",
    onClick,
    ...props
  },
  ref,
) {
  const innerRef = React.useRef<HTMLButtonElement | null>(null);
  const [lockedWidth, setLockedWidth] = React.useState<number | null>(null);

  // Ширина не должна прыгать при переходе в loading: фиксируем измеренную
  React.useLayoutEffect(() => {
    if (loading && innerRef.current && lockedWidth === null) {
      setLockedWidth(innerRef.current.getBoundingClientRect().width);
    }
    if (!loading && lockedWidth !== null) {
      setLockedWidth(null);
    }
  }, [loading, lockedWidth]);

  const Component = asChild ? Slot : "button";

  return (
    <Component
      ref={(node: HTMLButtonElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      type={asChild ? undefined : type}
      className={cn(
        buttonVariants({ variant, size, iconOnly }),
        loading && "pointer-events-none",
        className,
      )}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      style={lockedWidth !== null ? { minWidth: lockedWidth } : undefined}
      onClick={loading ? undefined : onClick}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 aria-hidden className="size-[14px] animate-spin" />
          <span className="sr-only">Выполняется…</span>
        </>
      ) : (
        icon
      )}
      {children}
      {trailingIcon}
    </Component>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, "iconOnly" | "children"> {
  /** Полное русское название действия: кнопка без текста иначе неозвучиваема. */
  label: string;
  icon: React.ReactNode;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = "ghost", ...props },
  ref,
) {
  return (
    <Button ref={ref} variant={variant} iconOnly aria-label={label} {...props}>
      {icon}
    </Button>
  );
});
